import { type BaseRendererComponentProps, BaseModel, BaseModelProps } from '@mail/3promo';
import { ReactRenderer } from '@mail/3promo/render/react';
import { LocalStorageStore } from '@mail/3promo/stores/localStorage';
import type { ModelCreator } from '@mail/3promo/types';
import React from 'react';
import { IS_IOS_MOBILE, IS_PHONE_BROWSER } from 'reactApp/appHelpers/configHelpers';
import { publicAnimatedTrendModal } from 'reactApp/appHelpers/featuresHelpers/features/3promoPublicAnimatedTrendModal';
import { PROMO_ID, publicBenefitsOfAuthorizationPromo } from 'reactApp/appHelpers/featuresHelpers/features/publicBenefitsOfAuthorization';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { store as reduxStore } from 'reactApp/store';

class PopupModel extends BaseModel<BaseModelProps> {
    getParent(): HTMLElement | null {
        return document.documentElement;
    }
}

const LazyComponent = React.lazy(() => import('reactApp/ui/PublicBenefitsOfAuthorizationPromo/PublicBenefitsOfAuthorizationPromo'));

const SuspendedComponent = <P extends BaseRendererComponentProps>(props: P) => {
    return (
        <React.Suspense fallback={null}>
            <LazyComponent {...props} />
        </React.Suspense>
    );
};

export const publicBenefitsOfAuthorizationPromoCreator: ModelCreator = ({ models, render, stores }) =>
    new PopupModel(
        PROMO_ID,
        models.base,
        new BaseModelProps(PROMO_ID, {
            store: new LocalStorageStore(PROMO_ID, stores.localStorage),
            renderer: new ReactRenderer(PROMO_ID, SuspendedComponent, render.react),
            closable: true,
            showDelay: 5000,
            templateData: {},
            ignoreTestOnRedraw: true,
            markAsCompleted: false,
            ignoreTimeWhenShowed: true,
            ignorePromoShowInterval: true,
            test: (resolve, reject) => {
                if (!IS_PHONE_BROWSER || IS_IOS_MOBILE) {
                    reject();
                    return;
                }
                if (sessionStorage.getItem(PROMO_ID)) {
                    reject();
                    return;
                }

                const state = reduxStore.getState();
                const storage = getCurrentStorage(state);

                // Если в экспе с анимационными модалками, то не показываем
                if (publicAnimatedTrendModal && publicAnimatedTrendModal.type !== 'e') {
                    reject();
                    return;
                }

                // Проверяем, что мы в эксперименте publicBenefitsOfAuthorization
                if (!publicBenefitsOfAuthorizationPromo) {
                    reject();
                    return;
                }

                // Показываем только в public
                if (storage !== EStorageType.public) {
                    reject();
                    return;
                }

                // Не показываем авторизованным пользователям
                const isAnonymous = UserSelectors.isAnonymous(state);

                if (isAnonymous) {
                    resolve();
                    return;
                }

                reject();
            },
            onClose() {
                sessionStorage.setItem(PROMO_ID, 'true');
            },
            onHide() {
                sessionStorage.setItem(PROMO_ID, 'true');
            },
            onShow(model) {
                model.reset();
            },
        })
    );
